import { useSelector } from "react-redux"
import { User } from "../../mp-database"
import { RootState } from "../redux/store"
import { FeedbackEvent, useFeedbackRequest } from "./useFeedbackRequest"
import { useEffect, useState } from "react"
import { useFeedbackTraySubmit } from "./useFeedbackTraySubmit"
import { enqueueSnackbar } from "notistack"
import { LAST_USER_INITIATED_FEEDBACK_TIMESTAMP } from "./feedbackConstants"

const setLastFeedbackTimestamp = (timestamp: number) => {
  localStorage.setItem(LAST_USER_INITIATED_FEEDBACK_TIMESTAMP, `${timestamp}`)
}

const getLastFeedbackTimestamp = () => {
  return Number(
    localStorage.getItem(LAST_USER_INITIATED_FEEDBACK_TIMESTAMP) || 0
  )
}

const useCheckFeedbackTimeout = () => {
  const [feedbackTimeoutDisabled, setFeedbackTimeoutDisabled] =
    useState<boolean>(false)

  const disableFeedback = () => setFeedbackTimeoutDisabled(true)

  useEffect(() => {
    const checkLastTimestamp = () => {
      const lastTimestamp = getLastFeedbackTimestamp()

      if (!lastTimestamp) {
        setFeedbackTimeoutDisabled(false)

        return
      }

      const currentTime = Date.now()
      const timeDifference = currentTime - lastTimestamp

      if (timeDifference > 900000) {
        setFeedbackTimeoutDisabled(false)
      } else {
        setFeedbackTimeoutDisabled(true)
      }
    }

    checkLastTimestamp()

    const intervalId = setInterval(() => {
      checkLastTimestamp()

      if (!feedbackTimeoutDisabled) {
        clearInterval(intervalId)
      }
    }, 120000)

    return () => clearInterval(intervalId)
  }, [feedbackTimeoutDisabled])

  return { feedbackTimeoutDisabled, disableFeedback }
}

export const useInitiatedFeedback = () => {
  const { user } = useSelector((state: RootState) => ({
    user: state.users.testUser as User
  }))

  const [openFeedback, setOpenFeedback] = useState(false)

  const { feedbackTimeoutDisabled, disableFeedback } = useCheckFeedbackTimeout()

  const openFeedbackTray = () => setOpenFeedback(true)
  const closeFeedbackTray = () => setOpenFeedback(false)

  const submitSuccessfulAction = () => {
    setLastFeedbackTimestamp(new Date().getTime())

    disableFeedback()

    closeFeedbackTray()
  }

  const requestFailedAction = () => {
    closeFeedbackTray()
    enqueueSnackbar(
      "Unable to send feedback. Please try again or contact customer support if you continue to have difficulties.",
      { variant: "error" }
    )
  }

  const { loading: feedbackLoading, feedbackPrompt } = useFeedbackRequest(
    user,
    FeedbackEvent.USER_INITIATED_FEEDBACK,
    {
      triggerRequestFlag: openFeedback,
      failedAction: requestFailedAction
    }
  )

  const { submitLoading, onSubmit } = useFeedbackTraySubmit(
    user,
    feedbackPrompt,
    {
      successfulAction: submitSuccessfulAction
    }
  )

  return {
    feedbackPrompt,
    openFeedback,
    submitLoading,
    feedbackLoading,
    feedbackTimeoutDisabled,
    onSubmit,
    openFeedbackTray,
    closeFeedbackTray
  }
}
